/**
 * PriceSetupPage 定價設定 (S9.1) — Board D #9, 系統 System nav.
 *
 * Tabs are ROUTE-DERIVED (`/auth/pricing/setup/:tab`, tab =
 * matrix|overrides|cost-basis|currency) — selection lives in the URL, never
 * component state (house selection tenet; ApPage precedent).
 *
 * - 價格群組 GroupMatrixTab: Group A–D × Source × unlimited user-defined
 *   levels; matrix currency is a USER SELECTOR (W9b「使用者選擇」). S5.2 adds
 *   the 定價群組 + 數量定價排程 admin cards INSIDE this tab (quick-create
 *   rows, TaxonomyQuickCreateRow state matrix — no new routes, Appendix D #14).
 * - 客戶特殊價 OverridesTab: BOTH fixed-price and override-% variants,
 *   validity windows, every save via ReasonRequiredModal → PRICE_OVERRIDE
 *   audit rows.
 * - 成本基準 CostBasisTab: per-product landed table, Purchase Cost read-only,
 *   edits manager-gated. S5.2 adds the read-only FX-rates read-through card
 *   (財務記帳與結算-only; dash + stale badge, never zero-as-data).
 * - 幣別價格 CurrencyTab (S2.2): the manually-locked per-currency price rows
 *   (人工鎖定, Discribe-19/21) — read view open, write controls behind
 *   `canEditCostBasis()` (DA-7 hide-don't-disable).
 *
 * Versioned price lists (draft→active approval, diff-vs-active, bulk adjust)
 * are DESCOPED — cut by default, not cut-if-pressed (plan §S9.1);
 * `SpecialItemPrice` validity windows cover effective-dating for the demo.
 * Reinstate only if 林芊's pricing-chain blessing explicitly requires it.
 */

import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import GroupMatrixTab from './GroupMatrixTab';
import OverridesTab from './OverridesTab';
import CostBasisTab from './CostBasisTab';
import CurrencyTab from './CurrencyTab';

export type PriceSetupTab = 'matrix' | 'overrides' | 'cost-basis' | 'currency';

// eslint-disable-next-line react-refresh/only-export-components -- vitest-pinned tab union (currencyTab.test.tsx)
export const PRICE_SETUP_TABS: readonly PriceSetupTab[] = [
  'matrix',
  'overrides',
  'cost-basis',
  'currency',
] as const;

const TAB_LABEL_KEY: Record<PriceSetupTab, string> = {
  matrix: 'pricing.tabs.matrix',
  overrides: 'pricing.tabs.overrides',
  'cost-basis': 'pricing.tabs.costBasis',
  currency: 'pricing.tabs.currency',
};

const PriceSetupPage = () => {
  const navigate = useNavigate();
  const { tab: tabParam } = useParams<{ tab: string }>();
  const { t } = useTranslation(['pricing', 'common']);

  // Route-derived tab — unknown segments bounce back to the matrix.
  const tab = PRICE_SETUP_TABS.find((candidate) => candidate === tabParam) ?? null;

  if (!tab) {
    return <Navigate to="/auth/pricing/setup/matrix" replace />;
  }

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('pricing:pricing.title')} />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(`/auth/pricing/setup/${key}`)}
        items={PRICE_SETUP_TABS.map((key) => ({
          key,
          label: t(`pricing:${TAB_LABEL_KEY[key]}`),
        }))}
      />
      {tab === 'matrix' && <GroupMatrixTab />}
      {tab === 'overrides' && <OverridesTab />}
      {tab === 'cost-basis' && <CostBasisTab />}
      {tab === 'currency' && <CurrencyTab />}
    </div>
  );
};

export default PriceSetupPage;
